import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  InformationCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon 
} from '@heroicons/react/24/solid'; // Using Heroicons for a clean look

/**
 * Reusable Banner/Alert component.
 * @param {object} props
 * @param {'info' | 'warning' | 'error'} props.variant - The style variant.
 * @param {string} props.title - Bold title for the banner.
 * @param {string} props.message - Descriptive text.
 * @param {boolean} [props.showIcon=true] - Whether to display the status icon.
 */
export default function Banner({ 
  variant = 'info', 
  title, 
  message, 
  showIcon = true 
}) {
    const { t } = useTranslation();

    // Configuration for different banner states
    const styles = {
        info: {
            container: 'bg-blue-50 border-blue-400 text-blue-800',
            icon: <InformationCircleIcon className="h-5 w-5 text-blue-400" />,
        },
        warning: {
            container: 'bg-yellow-50 border-yellow-400 text-yellow-800',
            icon: <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />,
        },
        error: {
            container: 'bg-red-50 border-red-400 text-red-800',
            icon: <XCircleIcon className="h-5 w-5 text-red-400" />,
        },
    };

    const currentStyle = styles[variant] || styles.info;

    return (
        <div className={`border-l-4 p-4 rounded-md ${currentStyle.container} shadow-sm`} role="alert">
            <div className="flex">
                {showIcon && (
                    <div className="flex-shrink-0">
                        {currentStyle.icon}
                    </div>
                )}
                <div className={showIcon ? 'ml-3' : ''}>
                    {title && (
                        <h3 className="text-sm font-bold">
                            {title}
                        </h3>
                    )}
                    <div className="text-sm mt-1">
                        <p>{message}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}